import {
  WSFlexBox,
  WSIcon,
  WSList,
  WSProgressBar,
  WSText
} from "@wingspanhq/fe-component-library";
import { BatchStatus, BatchSummaryExpand } from "@wingspanhq/wingspan-api-sdk";
import { formatNumber } from "accounting";
import { queriesBatches } from "@query";
import React, { ReactNode } from "react";

import { IMPORT_PROGRESS_REFETCH_INTERVAL } from "../../constants";

export function ImportBatchProgress({ batchId }: { batchId: string }) {
  const queryBatch = queriesBatches.getBatch.useQuery(
    { batchId },
    {
      refetchInterval: batch =>
        batch?.status === BatchStatus.PENDING ||
        batch?.status === BatchStatus.PROCESSING
          ? IMPORT_PROGRESS_REFETCH_INTERVAL
          : false
    }
  );
  const batch = queryBatch.data;
  const isImporting =
    batch?.status === BatchStatus.PENDING ||
    batch?.status === BatchStatus.PROCESSING;

  const querySummary = queriesBatches.getBatchSummary.useQuery(
    { batchId, expand: [BatchSummaryExpand.BREAKDOWN] },
    { refetchInterval: isImporting ? IMPORT_PROGRESS_REFETCH_INTERVAL : false }
  );

  const progress = querySummary.data?.progress;

  if (!batch || !progress) {
    return null;
  }

  const totalItems = progress.total ?? 0;
  const itemsProcessed = progress.completed ?? 0;
  const itemsFailed = progress.failed ?? 0;
  const processedItems = itemsProcessed + itemsFailed;

  const formattedTotalItems = formatNumber(totalItems);
  const formattedProcessedItems = formatNumber(processedItems);
  const formattedItemsProcessed = formatNumber(itemsProcessed);
  const formattedItemsFailed = formatNumber(itemsFailed);

  const progressTextMap: { [key in BatchStatus]: ReactNode } = {
    [BatchStatus.CREATED]: "",
    [BatchStatus.PENDING]: "Pending, import will begin shortly...",
    [BatchStatus.PROCESSING]: `${formattedProcessedItems} of ${formattedTotalItems} items imported. If you close this page, the progress will continue until it is completed.`,
    [BatchStatus.COMPLETED]: (
      <WSList gap="L" mt="M">
        <WSFlexBox.CenterY>
          <WSIcon block name="check-circle" color="green500" mr="XS" />
          <WSText.ParagraphSm color="gray500">
            {formattedItemsProcessed} of {formattedTotalItems} items imported
          </WSText.ParagraphSm>
        </WSFlexBox.CenterY>

        {itemsFailed > 0 && (
          <WSFlexBox.CenterY>
            <WSIcon block name="exit-circle" color="red400" mr="XS" />
            <WSText.ParagraphSm color="gray500">
              {formattedItemsFailed} of {formattedTotalItems} items failed
            </WSText.ParagraphSm>
          </WSFlexBox.CenterY>
        )}
      </WSList>
    ),
    [BatchStatus.FAILED]: (
      <WSFlexBox.CenterY>
        <WSIcon block name="exit-circle" color="red400" mr="XS" />
        <WSText.ParagraphSm color="gray500" py="M">
          {formattedItemsFailed} of {formattedTotalItems} items failed
        </WSText.ParagraphSm>
      </WSFlexBox.CenterY>
    )
  };

  const importPercent = totalItems ? (processedItems * 100) / totalItems : 0;

  return (
    <WSProgressBar
      percent={importPercent || 0}
      text={batch.status ? progressTextMap[batch.status] : ""}
      noAnimation={!isImporting}
    />
  );
}
